import { NestApiType, nestCall } from '@/composables/nestApi';
import { Logout } from '@/composables/User';

export type ShopifySsoResult =
  | { status: 'success'; idShop: number }
  | { status: 'link_required'; link?: string }
  | { status: 'not_provisioned' | 'error' };

// Same steps as the old Shopify login (route shopify.auto.auth): close any current session, then client_auth checks
// the connector's assertion and sets the session cookies. The caller loads the user (Me) as before.
export async function loginWithShopifySso(assertion: string): Promise<ShopifySsoResult> {
  await Logout();

  const res: any = await nestCall('post', NestApiType.AUTH, '/shopify/sso', {}, '?want_cookie=true', { assertion }, false);
  if (res && res.success && Number(res.idShop) > 0) {
    return { status: 'success', idShop: Number(res.idShop) };
  }

  const reason = res?.error?.message;
  if (reason === 'shopify_link_required') {
    // The id of the link request ("compte à relier") the page offers, when client_auth could keep one.
    const link = typeof res?.error?.link === 'string' ? res.error.link : undefined;
    return link ? { status: 'link_required', link } : { status: 'link_required' };
  }
  if (reason === 'shopify_shop_not_provisioned') {
    return { status: 'not_provisioned' };
  }
  return { status: 'error' };
}

// "Compte à relier": an account already has the Shopify shop's email. client_auth keeps a link request 15 minutes;
// the merchant logs in to that account to add the shop, or creates another account with an address confirmed by a
// code. Each answer below is mapped from client_auth's message; an expired request and one dropped after too many
// wrong passwords or codes end the same way (reopen the app from Shopify).

// newAccount: choice 2 is offered (a request of the new connector; the old connector's link can be replayed).
export type ShopifyLinkInfo = { status: 'ok'; shop: string; maskedEmail: string; newAccount: boolean } | { status: 'expired' | 'error' };

export type ShopifyLinkSession =
  | { status: 'success'; idShop: number }
  | { status: 'bad_credentials' | 'code_invalid'; attemptsLeft: number | null }
  | { status: 'email_taken' | 'shop_taken' | 'expired' | 'error' };

export type ShopifyLinkCode =
  | { status: 'code_sent'; maskedEmail: string }
  | { status: 'email_taken' | 'email_collaborator' | 'email_invalid' | 'too_many_codes' | 'expired' | 'error' };

const EXPIRED_MESSAGES = ['shopify_link_expired', 'shopify_link_too_many_attempts', 'shopify_link_no_code'];

const reasonOf = (res: any): string | undefined => res?.error?.message;

export async function getShopifyLinkInfo(link: string): Promise<ShopifyLinkInfo> {
  const res: any = await nestCall('post', NestApiType.AUTH, '/shopify/link/info', {}, '', { link }, false);
  if (res && res.success && typeof res.shop === 'string') {
    return {
      status: 'ok', shop: res.shop, maskedEmail: String(res.maskedEmail ?? ''), newAccount: res.newAccount === true,
    };
  }
  return EXPIRED_MESSAGES.includes(reasonOf(res) ?? '') ? { status: 'expired' } : { status: 'error' };
}

// Choice 1 and choice 2's last step both open the session on the new shop, like the Shopify login.
function sessionResult(res: any): ShopifyLinkSession {
  if (res && res.success && Number(res.idShop) > 0) {
    return { status: 'success', idShop: Number(res.idShop) };
  }
  const reason = reasonOf(res);
  const attemptsLeft = Number.isInteger(res?.error?.attemptsLeft) ? Number(res.error.attemptsLeft) : null;
  switch (reason) {
    case 'shopify_link_bad_credentials':
      return { status: 'bad_credentials', attemptsLeft };
    case 'shopify_link_code_invalid':
      return { status: 'code_invalid', attemptsLeft };
    case 'shopify_link_email_taken':
      return { status: 'email_taken' };
    case 'shopify_link_shop_taken':
      return { status: 'shop_taken' };
    default:
      return EXPIRED_MESSAGES.includes(reason ?? '') ? { status: 'expired' } : { status: 'error' };
  }
}

export async function linkShopifyShopWithPassword(link: string, email: string, password: string): Promise<ShopifyLinkSession> {
  await Logout();
  const res: any = await nestCall('post', NestApiType.AUTH, '/shopify/link/login', {}, '?want_cookie=true', { link, email, password }, false);
  return sessionResult(res);
}

export async function sendShopifyLinkCode(link: string, email: string): Promise<ShopifyLinkCode> {
  const res: any = await nestCall('post', NestApiType.AUTH, '/shopify/link/account', {}, '', { link, email }, false);
  if (res && res.success) {
    return { status: 'code_sent', maskedEmail: String(res.maskedEmail ?? '') };
  }
  switch (reasonOf(res)) {
    case 'shopify_link_email_taken':
      return { status: 'email_taken' };
    case 'shopify_link_email_collaborator':
      return { status: 'email_collaborator' };
    case 'shopify_link_email_invalid':
      return { status: 'email_invalid' };
    case 'shopify_link_too_many_codes':
      return { status: 'too_many_codes' };
    default:
      return EXPIRED_MESSAGES.includes(reasonOf(res) ?? '') ? { status: 'expired' } : { status: 'error' };
  }
}

export async function confirmShopifyLinkCode(link: string, code: string): Promise<ShopifyLinkSession> {
  await Logout();
  const res: any = await nestCall('post', NestApiType.AUTH, '/shopify/link/account/confirm', {}, '?want_cookie=true', { link, code }, false);
  return sessionResult(res);
}
